import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserCheck, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { ConfirmDayButton } from './ConfirmDayButton';
import { posLabel } from './crewPositions';
import { fmtDay } from './tenantTime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export async function confirmCrew(flightId) {
    try {
        const r = await axios.post(`${API}/flights/${flightId}/crew/confirm`);
        toast.success(`Tripulación confirmada (${r.data.confirmed})`);
        return true;
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo confirmar'); return false; }
}

// Bloque C: vuelos con tripulación propuesta pendiente de confirmar (CCO y pantalla de vuelos)
export function UnconfirmedCrewCard({ onChanged, alwaysShow = false }) {
    const { hasRole } = useAuth();
    const [items, setItems] = useState([]);
    const canConfirm = hasRole(['admin', 'crew_admin', 'ccod']);
    const load = useCallback(() => axios.get(`${API}/flights-unconfirmed-crew`).then(r => setItems(r.data)).catch(() => setItems([])), []);
    useEffect(() => { load(); }, [load]);
    if (!items.length) {
        return alwaysShow ? (
            <div className="rounded-lg border border-border/40 p-4 text-sm text-muted-foreground flex items-center gap-2" data-testid="unconfirmed-crew-empty">
                <UserCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />Tripulación sin confirmar: no hay vuelos pendientes
            </div>
        ) : null;
    }
    const days = [...new Set(items.map(f => f.date_local))];
    const label = p => p.flight_position ? `${p.name} (${posLabel(p.flight_position)}${p.in_training ? ', instrucción' : ''})` : p.name;
    return (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 space-y-3" data-testid="unconfirmed-crew-card">
            <p className="text-sm font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />Vuelos con tripulación sin confirmar ({items.length})</p>
            {days.map(d => (
                <div key={d} className="space-y-2" data-testid={`unconfirmed-day-${d}`}>
                    <div className="flex items-center gap-2 border-b border-amber-500/20 pb-1">
                        <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{fmtDay(d)}</span>
                        {canConfirm && <span className="ml-auto"><ConfirmDayButton date={d} onDone={() => { load(); onChanged?.(); }} /></span>}
                    </div>
                    {items.filter(f => f.date_local === d).map(f => (
                        <div key={f.id} className="flex flex-wrap items-center gap-3 text-sm" data-testid={`unconfirmed-${f.id}`}>
                            <span className="font-mono font-bold">{f.flight_number}</span>
                            <span className="font-mono text-xs">{f.origin}-{f.destination} · {f.departure_local || String(f.scheduled_departure).slice(0, 16).replace('T', ' ')}</span>
                            {f.hours_to_departure !== null && f.hours_to_departure < 3 && <span className="text-xs font-semibold text-red-600 dark:text-red-400">Sale en menos de 3 h</span>}
                            <span className="text-xs text-muted-foreground">{f.pending.map(label).join(', ')}</span>
                            {canConfirm && <Button size="sm" variant="outline" className="h-7 ml-auto" onClick={async () => { if (await confirmCrew(f.id)) { load(); onChanged?.(); } }} data-testid={`confirm-crew-${f.id}`}><UserCheck className="w-3.5 h-3.5 mr-1" />Confirmar tripulación</Button>}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}
